// Helpers puros para extrair variáveis e construir o payload Meta a partir do estado do form.

export const POSITIONAL_VAR_REGEX_G = /\{\{(\d+)\}\}/g;

export function extractVarIndices(text) {
  if (!text || typeof text !== "string") return [];
  const set = new Set();
  let m;
  const re = new RegExp(POSITIONAL_VAR_REGEX_G);
  while ((m = re.exec(text)) !== null) {
    set.add(parseInt(m[1], 10));
  }
  return Array.from(set).sort((a, b) => a - b);
}

export function isSequentialFromOne(indices) {
  if (indices.length === 0) return true;
  for (let i = 0; i < indices.length; i++) {
    if (indices[i] !== i + 1) return false;
  }
  return true;
}

export function nextVarNumber(text) {
  const indices = extractVarIndices(text);
  if (indices.length === 0) return 1;
  return indices[indices.length - 1] + 1;
}

/**
 * Monta o payload no formato esperado pela Meta (e validado em validateMetaTemplatePayload).
 *
 * Estado de entrada (form):
 * {
 *   name, language, category, allowCategoryChange,
 *   header: {
 *     enabled, format ('TEXT'|'IMAGE'|'VIDEO'|'DOCUMENT'),
 *     text, examples: { 1: 'valor' },
 *     media: { localUrl, mediaMetaId, fileName }
 *   },
 *   body: { text, examples: { 1, 2, ... } },
 *   footer: { enabled, text },
 *   buttons: { enabled, items: [{ type, text, url, phone_number, example }] }
 * }
 */
export function buildTemplatePayload(form, mode = "create") {
  const components = [];

  if (form.header?.enabled) {
    if (form.header.format === "TEXT") {
      const headerText = (form.header.text || "").trim();
      if (headerText) {
        const headerComp = {
          type: "HEADER",
          format: "TEXT",
          text: headerText
        };
        const indices = extractVarIndices(headerText);
        if (indices.length > 0) {
          headerComp.example = {
            header_text: indices.map(i => String(form.header.examples?.[i] || "").trim())
          };
        }
        components.push(headerComp);
      }
    } else {
      const handle =
        form.header.media?.resumableHandle ||
        form.header.media?.localUrl ||
        form.header.media?.mediaMetaId;
      if (handle) {
        components.push({
          type: "HEADER",
          format: form.header.format,
          example: { header_handle: [handle] }
        });
      }
    }
  }

  const bodyText = form.body?.text || "";
  const bodyComp = { type: "BODY", text: bodyText };
  const bodyIndices = extractVarIndices(bodyText);
  if (bodyIndices.length > 0) {
    bodyComp.example = {
      body_text: [bodyIndices.map(i => String(form.body?.examples?.[i] || "").trim())]
    };
  }
  components.push(bodyComp);

  if (form.footer?.enabled) {
    const footerText = (form.footer.text || "").trim();
    if (footerText) {
      components.push({ type: "FOOTER", text: footerText });
    }
  }

  if (form.buttons?.enabled && Array.isArray(form.buttons.items) && form.buttons.items.length > 0) {
    const buttons = form.buttons.items
      .map(b => {
        const out = {
          type: b.type,
          text: String(b.text || "").trim()
        };
        if (b.type === "URL") {
          out.url = String(b.url || "").trim();
          if (out.url.includes("{{")) {
            out.example = [String(b.example || "").trim()];
          }
        } else if (b.type === "PHONE_NUMBER") {
          out.phone_number = String(b.phone_number || "").trim();
        } else if (b.type === "COPY_CODE") {
          out.example = [String(b.example || "").trim()];
        }
        return out;
      })
      .filter(b => b.text || b.type === "COPY_CODE");

    if (buttons.length > 0) {
      components.push({ type: "BUTTONS", buttons });
    }
  }

  const payload = {
    category: form.category,
    components
  };

  if (mode === "create") {
    payload.name = String(form.name || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "_");
    payload.language = String(form.language || "pt_BR").trim();
    if (form.allowCategoryChange) payload.allow_category_change = true;
  }

  return payload;
}

/**
 * Faz o caminho inverso: a partir de um template (vindo da Meta ou do banco local),
 * popula o estado do form para edição.
 */
export function templateToFormState(template) {
  const components = Array.isArray(template?.components) ? template.components : [];

  const header = components.find(c => String(c.type).toUpperCase() === "HEADER");
  const body = components.find(c => String(c.type).toUpperCase() === "BODY");
  const footer = components.find(c => String(c.type).toUpperCase() === "FOOTER");
  const buttonsBlock = components.find(c => String(c.type).toUpperCase() === "BUTTONS");

  const form = {
    name: template?.name || "",
    language: template?.language || "pt_BR",
    category: (template?.category || "UTILITY").toUpperCase(),
    allowCategoryChange: false,
    header: {
      enabled: !!header,
      format: (header?.format || "TEXT").toUpperCase(),
      text: header?.text || "",
      examples: {},
      media: { localUrl: "", mediaMetaId: "", resumableHandle: "", fileName: "" }
    },
    body: { text: body?.text || "", examples: {} },
    footer: { enabled: !!footer?.text, text: footer?.text || "" },
    buttons: {
      enabled: !!buttonsBlock?.buttons?.length,
      items: Array.isArray(buttonsBlock?.buttons)
        ? buttonsBlock.buttons.map(b => ({
            type: (b.type || "QUICK_REPLY").toUpperCase(),
            text: b.text || "",
            url: b.url || "",
            phone_number: b.phone_number || "",
            example: Array.isArray(b.example) ? b.example[0] || "" : b.example || ""
          }))
        : []
    }
  };

  if (header?.format === "TEXT" && Array.isArray(header.example?.header_text)) {
    const indices = extractVarIndices(header.text || "");
    indices.forEach((idx, i) => {
      form.header.examples[idx] = header.example.header_text[i] || "";
    });
  } else if (header && header.format !== "TEXT") {
    const handle = header.example?.header_handle?.[0];
    if (handle) {
      if (String(handle).startsWith("4:")) {
        form.header.media.resumableHandle = handle;
      } else {
        form.header.media.localUrl = handle;
      }
    }
  }

  if (body && Array.isArray(body.example?.body_text?.[0])) {
    const indices = extractVarIndices(body.text || "");
    indices.forEach((idx, i) => {
      form.body.examples[idx] = body.example.body_text[0][i] || "";
    });
  }

  return form;
}

export const initialFormState = () => ({
  name: "",
  language: "pt_BR",
  category: "UTILITY",
  allowCategoryChange: true,
  header: {
    enabled: false,
    format: "TEXT",
    text: "",
    examples: {},
    media: { localUrl: "", mediaMetaId: "", resumableHandle: "", fileName: "" }
  },
  body: { text: "", examples: {} },
  footer: { enabled: false, text: "" },
  buttons: { enabled: false, items: [] }
});
